import "./BranchMappingEditor.scss";
import { ChangeEvent } from "react";
import { Button, Icon, TextField } from "../../shared/components";

export type BranchMapping = {
    pattern: string;
    url: string;
};

type BranchMappingEditorProps = {
    entries: BranchMapping[];
    updateEntries: (newEntries: BranchMapping[]) => void;
}

export const BranchMappingEditor = (props: BranchMappingEditorProps) => {
    const { entries, updateEntries } = props;

    const createRow = () => {
        const newEntry = { pattern: "*", url: "" };
        updateEntries([...(entries || []), newEntry]);
    }

    const deleteRow = (index: number) => () => {
        const newEntries = entries.filter((_, i) => i !== index);
        updateEntries(newEntries);
    }

    const getFieldValue = (index: number, fieldName: string) => {
        return entries?.[index]?.[fieldName];
    }

    const setFieldValue = (index: number, fieldName: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const rows = entries.slice();
        rows[index][fieldName] = event.target.value;
        updateEntries(rows);
    }

    const moveUp = (index: number) => () => {
        if (index === 0)
            return;

        updateEntries([
            ...entries.slice(0, index - 1), // Unaffected items before the swap location
            entries[index],                 // The selected item moves up 1 position
            entries[index - 1],             // The predecessor moves down 1 position
            ...entries.slice(index + 1)     // Unaffected items after the swap location
        ]);
    }

    const moveDown = (index: number) => () => {
        if (index === entries.length - 1)
            return;

        updateEntries([
            ...entries.slice(0, index),     // Unaffected items before the swap location
            entries[index + 1],             // The successor moves up 1 position
            entries[index],                 // The selected item moves down 1 position
            ...entries.slice(index + 2)     // Unaffected items after the swap location
        ]);
    }

    return (
        <div className="branch-mapping-editor">
            {
                entries?.length > 0 ?
                    <table className="table compact striped bordered">
                        <thead>
                            <tr>
                                <th style={{ width: 56 }}>&nbsp;</th>
                                <th style={{ width: 56 }}>&nbsp;</th>
                                <th style={{ width: 200 }}>Branch Name Pattern</th>
                                <th>URL (Must be Publicly Accessible)</th>
                                <th style={{ width: 56 }}>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                entries.map((_, index) =>
                                    <tr key={`row-${index}`}>
                                        <td>
                                            <Button data-type="move-up" variant="contained" color="info" onClick={moveUp(index)} size="small" title="Move Up">
                                                <Icon fontSize="small">arrow_upward</Icon>
                                            </Button>
                                        </td>
                                                                                <td>
                                            <Button data-type="move-down" variant="contained" color="info" onClick={moveDown(index)} size="small" title="Move Down">
                                                <Icon fontSize="small">arrow_downward</Icon>
                                            </Button>
                                        </td>
                                        {
                                            ["pattern", "url"].map((key, cellIndex) =>
                                                <td key={`branch-mapping-${index}-${cellIndex}`}>
                                                    <TextField
                                                        size="small"
                                                        variant="outlined"
                                                        fullWidth
                                                        value={getFieldValue(index, key)}
                                                        onChange={setFieldValue(index, key)}
                                                    />
                                                </td>
                                            )
                                        }
                                        <td>
                                            <Button variant="contained" color="error" onClick={deleteRow(index)} size="small" title="Delete Row">
                                                <Icon fontSize="small">delete</Icon>
                                            </Button>
                                        </td>
                                    </tr>
                                )
                            }
                        </tbody>
                    </table> :
                    <div className="no-data">No Mapping Rules Specified - Plugin will be ignored for ALL Branches.</div>
            }
            <div className="footer">
                <Button variant="contained" color="success" onClick={createRow} size="small" title="Add Row">
                    <Icon fontSize="small">add</Icon>
                </Button>
            </div>
        </div>
    );
}